import { useQuery } from '@apollo/client';
import moment from 'moment';
import { FC, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import CLOSE_ICON from '../../assets/images/close.svg';
import { ClipBoardIcon } from '../../assets/images/svgs';
import { AppContext } from '../../context';
import { ROLES } from '../../utils/constant';
import { moneyFormatter } from '../../utils/helper';
import { copySheetToClipboard } from '../../utils/sheetClipboard';
import Loader from '../layout/Loader';
import {
  ITempBillingPaidInvoiceRow,
  reasonLabel,
  TEMP_BILLING_ACTIVITY_REPORT_INVOICES,
  TEMP_BILLING_PAID_INVOICES,
  TEMP_BILLING_STATUS_LABEL,
} from './gql';

// kan-101: once a People 2.0 payment is reconciled, the executive wants the list
// of invoices it paid out of the app and into their own workbook. Structurally
// this mirrors the kan-89 WeeklyActivitySheetModal and shares its clipboard
// helper, so there is exactly one copy-to-Excel code path.
//
// The rows are served from the SETTLING-TIME SNAPSHOT, not from live imported
// rows — a weekly-activity import deleted six months from now must not change
// what this modal shows.
//
// kan-112: Accepted and Suspense invoices are settled too, so they are on the
// sheet "as they are", with Status / Reason / Note columns beside Match so
// whoever reads the paste can tell an explained reconciling item and a parked
// difference from a clean clear without coming back to the app.
//
// "Owed to RealREPP" / "Calc" sit right after Profit Summary so the three
// profit figures — what the weekly report says, what People 2.0 paid, what our
// rates say they owed — read side by side in the paste.

interface IPaidInvoicesModalProps {
  /**
   * MetaDataRecruiterProfitSummary id — ONE payment's sheet. Mutually exclusive
   * with `activityReportMonth`; exactly one of the two must be given.
   */
  paymentMetaId?: string;
  /**
   * 'YYYY-MM' — the sheet for EVERY payment tagged with this activity report. A
   * month's payout usually arrives as several ACHes, so this is the block the
   * executive actually reports.
   */
  activityReportMonth?: string;
  /** Overrides the default trigger label/title. */
  label?: string;
  /** Render the trigger as a labelled button rather than the bare clipboard icon. */
  withLabel?: boolean;
  /** Nothing has been settled yet, so there is nothing to list. */
  disabled?: boolean;
  /**
   * Trigger classes. The detail page drops the trigger into its own 28x28 icon
   * slot (`tbr-icon-btn`) rather than forking the modal; anywhere else gets the
   * plain borderless default.
   */
  className?: string;
}

interface ISheetColumn {
  name: string;
  /** What lands in the clipboard (raw, Excel-friendly). */
  value: string;
  /** What the modal shows; defaults to `value`. */
  display?: string;
}

const asText = (value?: string | null): string =>
  value === null || value === undefined ? '' : value;

// These are the NUMERIC snapshot columns, so they always format — unlike the raw
// varchar money on the imported rows, which can hold "(1234.00)".
const asMoneyText = (value?: number | null): string =>
  typeof value === 'number' && isFinite(value) ? value.toFixed(2) : '';

const PaidInvoicesModal: FC<IPaidInvoicesModalProps> = ({
  paymentMetaId,
  activityReportMonth,
  label,
  withLabel,
  disabled,
  className,
}) => {
  const { userRoles } = useContext(AppContext);
  const isExecutive = !!userRoles?.includes(ROLES.EXECUTIVE);

  const [open, setOpen] = useState<boolean>(false);

  const byActivity = !!activityReportMonth;

  // Two queries, one modal: the columns, the copy path and the rendering are
  // identical, only the scope differs. Forking the component would have meant two
  // copies of the sheet mapping to keep in step.
  const paymentQuery = useQuery<{
    tempBillingPaidInvoices: { rows: Array<ITempBillingPaidInvoiceRow> };
  }>(TEMP_BILLING_PAID_INVOICES, {
    variables: { paymentMetaId },
    // Only needed once the executive opens the modal.
    skip: !open || byActivity || !paymentMetaId,
    fetchPolicy: 'network-only',
  });

  const activityQuery = useQuery<{
    tempBillingActivityReportInvoices: {
      rows: Array<ITempBillingPaidInvoiceRow>;
      paymentCount: number;
      paymentReferenceIds: Array<string>;
    };
  }>(TEMP_BILLING_ACTIVITY_REPORT_INVOICES, {
    variables: { activityReportMonth },
    skip: !open || !byActivity || !activityReportMonth,
    fetchPolicy: 'network-only',
  });

  const { data, loading, error } = byActivity ? activityQuery : paymentQuery;

  if (!isExecutive || (!paymentMetaId && !activityReportMonth)) return null;

  const toggle = () => setOpen(!open);

  const activityResult = (data as any)?.tempBillingActivityReportInvoices;
  const rows: Array<ITempBillingPaidInvoiceRow> = byActivity
    ? activityResult?.rows || []
    : (data as any)?.tempBillingPaidInvoices?.rows || [];
  const paymentCount: number = activityResult?.paymentCount || 0;

  const rowToColumns = (row: ITempBillingPaidInvoiceRow): Array<ISheetColumn> => [
    // Only worth a column when the sheet spans more than one payment.
    ...(byActivity
      ? [{ name: 'Payment Ref', value: asText(row?.paymentReferenceId) }]
      : []),
    { name: 'Invoice Number', value: asText(row?.invoiceNumber) },
    { name: 'Client', value: asText(row?.companyName) },
    { name: 'Temp Employee', value: asText(row?.candidateName) },
    { name: 'Placed Id', value: asText(row?.placementId) },
    { name: 'Invoice Date', value: asText(row?.invoiceDate) },
    { name: 'Week Ending', value: asText(row?.periodEndDate) },
    {
      name: 'Invoice Amount',
      value: asMoneyText(row?.invoiceAmount),
      display: moneyFormatter(row?.invoiceAmount || 0),
    },
    {
      name: 'Weekly Activity Profit',
      value: asMoneyText(row?.weeklyActivityTotal),
      display: moneyFormatter(row?.weeklyActivityTotal || 0),
    },
    {
      name: 'Profit Summary',
      value: asMoneyText(row?.profitSummaryTotal),
      display: moneyFormatter(row?.profitSummaryTotal || 0),
    },
    // What our contract rates say People 2.0 owed us, frozen when the invoice
    // was settled. Blank when it could not be fully calculated then (a line
    // with no placement / state, or weeks before 1/1/2026).
    {
      name: 'Owed to RealREPP',
      value: asMoneyText(row?.owedTotal),
      display: typeof row?.owedTotal === 'number' ? moneyFormatter(row.owedTotal) : '',
    },
    {
      name: 'Calc',
      value: row?.calcMatched === true ? 'Match' : row?.calcMatched === false ? 'Off' : '',
    },
    { name: 'Match', value: row?.matched ? 'Match' : 'Mismatch' },
    {
      name: 'Status',
      value: row?.status ? TEMP_BILLING_STATUS_LABEL[row.status] || row.status : '',
    },
    { name: 'Reason', value: reasonLabel(row?.flagReason) },
    // A tab or line break inside a cell would split the row when pasted into
    // Excel, so the note is flattened to one line for the sheet.
    { name: 'Note', value: asText(row?.flagNote).replace(/\s+/g, ' ').trim() },
    {
      name: 'Settled On',
      value: row?.clearedAt ? moment(row?.clearedAt).format('MM/DD/YYYY') : '',
    },
    { name: 'Settled By', value: asText(row?.clearedByName) },
  ];

  // Header labels are the same for every row.
  const headerColumns: Array<ISheetColumn> = rowToColumns({
    invoiceNumber: '',
    invoiceAmount: 0,
    weeklyActivityTotal: 0,
    profitSummaryTotal: 0,
    matched: false,
    status: 'OPEN',
    clearedAt: '',
  });

  const copyAll = async () => {
    // No colored cells here, so this stays a plain tab-separated copy.
    const cells = rows.map((row) =>
      rowToColumns(row).map((column) => ({ text: column.value }))
    );
    const copied = await copySheetToClipboard(cells);
    if (copied) {
      toast.success(`${rows.length} rows copied — paste into the sheet`);
    } else {
      toast.error('Copy failed — please copy the values manually');
    }
  };

  return (
    <>
      <Button
        type="button"
        title={label || 'Paid Invoices'}
        disabled={disabled}
        // `link` only suppresses reactstrap's own btn-secondary paint; the caller's
        // class is what actually styles the trigger.
        color={className ? 'link' : undefined}
        className={className || 'bg-transparent border-0 p-0 mr-3'}
        onClick={toggle}
      >
        <span className="d-flex align-items-center" style={{ gap: '6px' }}>
          <ClipBoardIcon />
          {withLabel && <span>{label || 'Paid Invoices'}</span>}
        </span>
      </Button>

      <Modal
        isOpen={open}
        toggle={toggle}
        size="xl"
        className="revampDialog"
        style={{ maxWidth: '95vw' }}
      >
        <ModalHeader toggle={toggle}>
          <div className="d-flex align-items-center">
            <span>
              {byActivity
                ? `Paid Invoices — ${moment(activityReportMonth).format('MMM-YY')}`
                : 'Paid Invoices'}
            </span>
            {byActivity && !!paymentCount && (
              <small className="text-muted ml-2">
                {rows.length} row(s) across {paymentCount} payment(s)
              </small>
            )}
          </div>
          <button className="iconButton" onClick={toggle} type="button">
            <img src={CLOSE_ICON} alt="close" />
          </button>
        </ModalHeader>

        <ModalBody>
          {loading && <Loader loaderClass="sm-height" />}

          {!loading && error && (
            <p className="text-danger m-0">
              {error?.message || 'Unable to load the paid invoices'}
            </p>
          )}

          {!loading && !error && !rows.length && (
            <p className="text-muted m-0">
              {byActivity
                ? 'No invoices have been settled on any payment with this activity report yet'
                : 'No invoices have been settled on this payment yet'}
            </p>
          )}

          {!loading && !error && !!rows.length && (
            <div style={{ overflowX: 'auto' }}>
              <Table
                bordered
                size="sm"
                style={{ width: 'max-content', fontSize: '12px' }}
              >
                <thead>
                  <tr>
                    {headerColumns.map((column) => (
                      <th
                        key={column.name}
                        style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}
                      >
                        {column.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={`${row?.invoiceNumber}-${index}`}>
                      {rowToColumns(row).map((column) => {
                        const shown = column.display ?? column.value;
                        return (
                          <td key={column.name} style={{ whiteSpace: 'nowrap' }}>
                            {shown || <span className="text-muted">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button type="button" onClick={toggle} className="buttonPrimary mr-3">
            Close
          </button>
          <button
            type="button"
            onClick={copyAll}
            disabled={!rows.length}
            className="buttonGenericStyle filledButton"
          >
            Copy All
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PaidInvoicesModal;
